import { useCallback } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router';

import { useLanguage } from '../../contexts/language';
import { useSession } from '../../contexts/session';
import { useSignOut } from '../../hooks/sign-out';
import { useVisit } from '../../hooks/visit';

import { BirthDateStep } from '../birth-date-step';
import { Centered } from '../centered';
import { FiscalCodeStep } from '../fiscal-code-step';
import { GenderStep } from '../gender-step';
import { NameStep } from '../name-step';
import { StepProgress } from '../step-progress';

import styles from './style.module.css';

// The four onboarding steps in order, one screen each (C19). The path names
// the step so that Back and the browser's own history move between them; the
// router reads this list too, to send a returning person to the step they had
// reached.
export const steps = [ 'name', 'birth-date', 'gender', 'fiscal-code' ];

// Onboarding: the frame, the counter, and which step is on screen. Each step
// saves its own answers; this decides where to go afterwards.
//
// A step can be revisited but not skipped ahead of: asking for a later step
// than the first unanswered one lands on that one instead. Back from the first
// step signs out, since the only thing before it is the sign-in screen.
export function Onboarding() {
  const { text } = useLanguage();
  const { me, refresh } = useSession();
  const signOut = useSignOut();
  const navigate = useNavigate();
  const capture = useVisit();
  const { step } = useParams();
  const number = steps.indexOf(step) + 1;

  const onBack = useCallback(function() {
    if (number === 1) {
      return signOut();
    }

    return navigate(`/onboarding/${ steps[ number - 2 ] }`);
  }, [ number, signOut, navigate ]);

  // Saving moves forward: the session is asked again so the router knows how
  // far onboarding has got, then the next step opens. After the last step the
  // refreshed session is complete and the router opens the app on its own.
  // A step calls this only while its visit lasts, and the next step opens
  // only if onboarding is still on that visit once the session has answered
  // (#135): Previous pressed meanwhile stays where it led.
  const onDone = useCallback(function() {
    const owns = capture();

    return refresh().then(function() {
      if (owns() === true && number < steps.length) {
        navigate(`/onboarding/${ steps[ number ] }`);
      }
    });
  }, [ number, refresh, navigate, capture ]);

  if (number === 0 || number > me.onboardingStep) {
    return <Navigate to={ `/onboarding/${ steps[ me.onboardingStep - 1 ] }` } replace/>;
  }

  let screen = <NameStep profile={ me.profile } onBack={ onBack } onDone={ onDone }/>;

  if (number === 2) {
    screen = <BirthDateStep profile={ me.profile } onBack={ onBack } onDone={ onDone }/>;
  }

  if (number === 3) {
    screen = <GenderStep profile={ me.profile } onBack={ onBack } onDone={ onDone }/>;
  }

  if (number === 4) {
    screen = <FiscalCodeStep profile={ me.profile } onBack={ onBack } onDone={ onDone }/>;
  }

  return (
    <Centered>
      <div className={ styles.counter }>
        <span>{ text.yourProfile }</span>

        <span>{ text.stepOf.replace('{step}', number) }</span>
      </div>

      <StepProgress step={ number } total={ steps.length }/>

      { screen }
    </Centered>
  );
}
